import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@bookdental/ui";
import { faltandoNoCadastro, mensagemDeFaltando } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { useConfiguracaoDePacientes } from "@/lib/use-configuracao-de-pacientes";
import { AJUDA_PACIENTES } from "@/lib/ajuda/dialogos/pacientes";
import { useAuth } from "@/lib/auth";
import { useOwnedOrganizations } from "@/lib/owned-organizations";
import { useSwitchOrganization } from "@/lib/use-switch-organization";
import { CreateOrgPicker, type CreateOrgOption } from "@/components/shared/CreateOrgPicker";
import { PatientForm, EMPTY_PATIENT_FORM, type PatientFormValues } from "./PatientForm";
import { buildPatientPayload } from "./patientPayload";
import { EscolherOrganizacoesDoCadastro, type OrgDoCadastro } from "./EscolherOrganizacoesDoCadastro";

export type CreatedPatient = { id: string; name: string; phone: string | null };

type InitialOrgSnapshot = { id: string; name: string; isPersonal: boolean } | null;

type Props = {
  open: boolean;
  onClose: () => void;
  /** Pré-preenche o nome ao abrir — usado quando o dialog nasce de um nome já digitado
   *  em outro fluxo (ex: paciente não encontrado ao agendar consulta). */
  initialName?: string;
  onCreated?: (patient: CreatedPatient) => void;
};

export function PatientFormDialog({ open, onClose, initialName, onCreated }: Props) {
  const utils = trpc.useUtils();
  const config = useConfiguracaoDePacientes();
  const { user } = useAuth();
  const { clinics } = useOwnedOrganizations();
  const { switchTo } = useSwitchOrganization();
  const [values, setValues] = useState<PatientFormValues>(EMPTY_PATIENT_FORM);
  const [addToAllOrganizations, setAddToAllOrganizations] = useState(false);
  const [orgsEscolhidas, setOrgsEscolhidas] = useState<Set<string>>(new Set());
  const [initialOrg, setInitialOrg] = useState<InitialOrgSnapshot>(null);

  useEffect(() => {
    if (open) {
      setValues({ ...EMPTY_PATIENT_FORM, name: initialName ?? "" });
      setAddToAllOrganizations(false);
      setOrgsEscolhidas(new Set());
      setInitialOrg(
        user?.activeOrganization
          ? { id: user.activeOrganization.id, name: user.activeOrganization.name, isPersonal: user.activeOrganization.isPersonal }
          : null,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const orgOptions: CreateOrgOption[] =
    initialOrg?.isPersonal
      ? [
          { id: initialOrg.id, label: `${initialOrg.name} (Pessoal)` },
          ...clinics.filter((c) => c.id !== initialOrg.id).map((c) => ({ id: c.id, label: c.name })),
        ]
      : [];

  const revertOrgIfNeeded = () => {
    if (initialOrg && user?.activeOrganization?.id !== initialOrg.id) {
      switchTo(initialOrg.id).catch(() => {});
    }
  };

  const organizationsQuery = trpc.organization.listMine.useQuery(undefined, { enabled: open });
  const ownedOrganizationCount =
    organizationsQuery.data?.filter((m) => m.role === "OWNER" || m.role === "PARTNER").length ?? 0;

  // Atual sempre primeiro (e sempre marcada — o cadastro nasce nela); depois as próprias.
  const organizacoesDoCadastro: OrgDoCadastro[] = [
    ...(user?.activeOrganization
      ? [{ id: user.activeOrganization.id, name: user.activeOrganization.name, atual: true }]
      : []),
    ...(organizationsQuery.data ?? [])
      .filter((m) => (m.role === "OWNER" || m.role === "PARTNER") && m.organization.id !== user?.activeOrganization?.id)
      .map((m) => ({ id: m.organization.id, name: m.organization.name, atual: false })),
  ];
  const idsParaCadastro = organizacoesDoCadastro
    .filter((o) => o.atual || orgsEscolhidas.has(o.id))
    .map((o) => o.id);

  const create = trpc.patient.create.useMutation({
    onSuccess: (patient) => {
      toast.success(
        addToAllOrganizations && idsParaCadastro.length > 1
          ? `Paciente cadastrado em ${idsParaCadastro.length} organizações`
          : "Paciente cadastrado",
      );
      utils.patient.list.invalidate();
      utils.patient.listTags.invalidate();
      utils.patient.listMineAcrossOrgs.invalidate();
      setValues(EMPTY_PATIENT_FORM);
      setAddToAllOrganizations(false);
      setOrgsEscolhidas(new Set());
      onClose();
      onCreated?.({ id: patient.id, name: patient.name, phone: patient.phone });
    },
    onError: (err) => toast.error(err.message || "Falha ao criar paciente"),
  });

  // Obrigatórios de Configurações › Pacientes (o servidor confere de novo).
  const faltando = faltandoNoCadastro(config, { ...values }, { semCpf: values.estrangeiro });
  const canSave = values.name.trim().length >= 2 && faltando.length === 0 && !create.isPending;

  const handleCancel = () => {
    setValues(EMPTY_PATIENT_FORM);
    setAddToAllOrganizations(false);
    setOrgsEscolhidas(new Set());
    revertOrgIfNeeded();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleCancel()}>
      <DialogContent className="flex h-[100dvh] max-h-[100dvh] w-full max-w-2xl flex-col gap-0 overflow-hidden border-0 bg-white p-0 sm:h-auto sm:max-h-[92vh] sm:rounded-2xl sm:border sm:border-zinc-200">
        <DialogHeader className="border-b border-zinc-100 px-6 py-4 dark:border-border">
          <DialogTitle className="text-center" ajuda={AJUDA_PACIENTES.novoPaciente} classeDoGrupo="justify-center">Novo paciente</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-6">
          {orgOptions.length > 1 && (
            <div className="mb-4">
              <CreateOrgPicker options={orgOptions} />
            </div>
          )}

          <PatientForm
            values={values}
            onChange={(p) => setValues((prev) => ({ ...prev, ...p }))}
          />

          {ownedOrganizationCount > 1 && (
            <EscolherOrganizacoesDoCadastro
              organizacoes={organizacoesDoCadastro}
              ativo={addToAllOrganizations}
              onAtivoChange={(v) => {
                setAddToAllOrganizations(v);
                setOrgsEscolhidas(v ? new Set(organizacoesDoCadastro.map((o) => o.id)) : new Set());
              }}
              selecionadas={orgsEscolhidas}
              onToggle={(id) =>
                setOrgsEscolhidas((prev) => {
                  const next = new Set(prev);
                  if (next.has(id)) next.delete(id);
                  else next.add(id);
                  return next;
                })
              }
            />
          )}
        </div>

        {values.name.trim().length >= 2 && faltando.length > 0 && (
          <p className="border-t border-zinc-100 px-6 pt-3 text-center text-xs text-amber-700 dark:border-border dark:text-amber-400">{mensagemDeFaltando(faltando)}</p>
        )}
        <DialogFooter className="!flex-row items-center !justify-center gap-2 border-t border-zinc-100 p-4 sm:px-6 dark:border-border">
          <Button type="button" variant="ghost" onClick={handleCancel}>
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={() =>
              create.mutate({
                ...buildPatientPayload(values),
                addToAllOrganizations,
                organizationIds: addToAllOrganizations ? idsParaCadastro : undefined,
              })
            }
            disabled={!canSave}
          >
            {create.isPending ? "Salvando..." : "Cadastrar paciente"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
